import { notEmpty } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentSponsor } from "~/components/content/content-sponsor-tag/hooks";
import { checkIsMainTopic } from "~/components/content/helpers";
import { SponsorTag } from "~/components/sponsor-tag";
import type { StyledProps as SponsorTagStyledProps } from "~/components/sponsor-tag/styles";
import type { articleSponsorContent$key } from "~/queries/__generated__/articleSponsorContent.graphql";

import { StyledContentSchemaRender, StyledTooltip } from "./styles";

type Props = {
  className?: string;
  reference: articleSponsorContent$key;
  sponsorTagStyle?: Partial<PropsFromStyledProps<SponsorTagStyledProps>>;
};

export const ArticleSponsor: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  sponsorTagStyle,
}) => {
  const content = useFragment(
    graphql`
      fragment articleSponsorContent on Article {
        topics {
          entityId
          sponsor {
            description {
              json
            }
            ...sponsorTagSponsor
          }
        }
        ...hooksUseContentSponsorContent
        ...helpersCheckIsMainTopicContent
        ...contentSchemaRenderContent
      }
    `,
    reference_,
  );
  const sponsor = useContentSponsor(content);
  if (!content.topics || !sponsor) return null;

  const mainTopic = content.topics.find(topic => checkIsMainTopic(content, topic?.entityId));
  if (!mainTopic) return null;

  const sponsorTagElement = (
    <SponsorTag
      {...sponsorTagStyle}
      className={className}
      reference={sponsor}
      typeFontSize={sponsorTagStyle?.typeFontSize ?? "12px"}
      typeLineHeight={sponsorTagStyle?.typeLineHeight ?? "14px"}
    />
  );
  if (notEmpty(mainTopic.sponsor?.description?.json))
    return (
      <StyledTooltip
        placement="bottom-start"
        title={
          <StyledContentSchemaRender
            reference={content}
            schema={mainTopic.sponsor?.description?.json}
          />
        }
        arrow
      >
        {sponsorTagElement}
      </StyledTooltip>
    );

  return sponsorTagElement;
};

ArticleSponsor.displayName = "ArticleSponsor";
